import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

const commitments = [
  {
    number: "01",
    title: "Materials with a purpose",
    text: "We choose lower-impact fibers, prioritize certified sources and keep looking for better alternatives to conventional materials.",
  },
  {
    number: "02",
    title: "People before shortcuts",
    text: "Long-term partnerships, open conversations and safer working conditions are the basis of how our pieces are made.",
  },
  {
    number: "03",
    title: "Progress in public",
    text: "We share what works, where we still have work to do and why every decision across a garment's life matters.",
  },
];

function About() {
  const { brandName } = siteConfig;

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="about">
      <section className="about__hero" aria-labelledby="about-heading">
        <img
          className="about__hero-image"
          src="/main/impact-report.jpg"
          alt={`People moving together in a ${brandName} editorial campaign`}
        />
        <div className="about__hero-shade" />
        <div className="about__hero-copy">
          <p className="about__eyebrow">About {brandName}</p>
          <h1 id="about-heading">Fashion is a verb.</h1>
          <p>What we wear can move things forward.</p>
        </div>
      </section>

      <section className="about__intro" id="care">
        <p className="about__section-label">Our point of view</p>
        <h2>Made to make a difference.</h2>
        <div className="about__intro-copy">
          <p>
            {brandName} exists to prove that considered clothing can feel
            current, useful and full of character. We design everyday pieces
            with their entire journey in mind—from fiber to finish and far
            beyond one season.
          </p>
          <p>
            Responsibility is not a capsule collection. It is the work behind
            every collection: asking better questions, choosing partners with
            care and improving what we can measure.
          </p>
        </div>
      </section>

      <section
        className="about__editorial about__editorial--materials"
        id="materials"
      >
        <div className="about__editorial-image-wrap">
          <img
            src="/main/mission-materials.jpg"
            alt="Natural and recycled textile swatches arranged in a studio"
            loading="lazy"
          />
        </div>
        <div className="about__editorial-copy">
          <span className="about__index" aria-hidden="true">
            01
          </span>
          <p className="about__section-label">From fiber forward</p>
          <h2>Better materials. Fewer assumptions.</h2>
          <p>
            The right fabric has to do more than look good. We consider its
            origin, processing, durability and next life, then select the
            option that best balances comfort and impact.
          </p>
          <Link to="/shop" onClick={toTop} className="about__text-link">
            Explore the collection <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>

      <section
        className="about__statement"
        id="transparency"
        aria-label="Brand statement"
      >
        <p>Less noise.</p>
        <p>More honesty.</p>
        <p>Clothes made for change.</p>
      </section>

      <section className="about__editorial about__editorial--people" id="people">
        <div className="about__editorial-copy">
          <span className="about__index" aria-hidden="true">
            02
          </span>
          <p className="about__section-label">Made by people</p>
          <h2>A fairer future is built together.</h2>
          <p>
            A garment carries many hands and many decisions. We work toward
            durable relationships, clearer supply chains and workplaces where
            people are respected—not hidden behind the product.
          </p>
          <Link to="/contact" onClick={toTop} className="about__text-link">
            Talk to our team <span aria-hidden="true">↗</span>
          </Link>
        </div>
        <div className="about__editorial-image-wrap">
          <img
            src="/main/mission-people.jpg"
            alt="Garment makers collaborating around a cutting table"
            loading="lazy"
          />
        </div>
      </section>

      <section
        className="about__commitments"
        id="impact"
        aria-labelledby="commitments-heading"
      >
        <div className="about__commitments-heading">
          <p className="about__section-label">How we work</p>
          <h2 id="commitments-heading">Change, one decision at a time.</h2>
        </div>
        <div className="about__commitment-grid">
          {commitments.map((commitment) => (
            <article className="about__commitment" key={commitment.number}>
              <span>{commitment.number}</span>
              <h3>{commitment.title}</h3>
              <p>{commitment.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about__cta">
        <p className="about__section-label">Wear the change</p>
        <h2>Pieces for today. Thinking for tomorrow.</h2>
        <Link to="/shop" onClick={toTop} className="about__button">
          Shop all styles
        </Link>
      </section>
    </main>
  );
}

export default About;
